import { useState } from "react";
import { Building2, Stethoscope, Pill, FlaskConical, Package, ArrowRight, Droplets, Heart, Truck, CalendarHeart, Users } from "lucide-react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import PatientAuthModal from "@/components/patient/PatientAuthModal";
import { useAllModulesConfig } from "@/hooks/useAuthConfig";
import { isAdminPreview } from "@/hooks/useModuleVisibility";

const Categories = () => {
  const navigate = useNavigate();
  const [showPatientAuth, setShowPatientAuth] = useState(false);
  const moduleConfig = useAllModulesConfig();
  const adminPreview = isAdminPreview();
  const isModuleVisible = (key: string | null) => {
    if (!key) return true;
    if (adminPreview) return true;
    const status = moduleConfig?.[key]?.visibility_status ?? 'LIVE';
    return status === 'LIVE';
  };

  const allCategories = [
    {
      icon: Users,
      title: "Patient",
      description: "Manage your health records, appointments & prescriptions digitally.",
      count: "50K+",
      emoji: "👤",
      gradient: "from-primary to-medical-blue-light",
      onClick: () => setShowPatientAuth(true),
      moduleKey: 'patient' as string | null,
    },
    {
      icon: Stethoscope,
      title: "Doctors",
      description: "Connect with qualified doctors across various specializations.",
      count: "2000+",
      emoji: "🩺",
      gradient: "from-primary to-medical-blue-light",
      onClick: () => navigate("/listings?type=2"),
      moduleKey: 'doctor',
    },
    {
      icon: Building2,
      title: "Hospitals",
      description: "Find top-rated hospitals with best facilities near you.",
      count: "500+",
      emoji: "🏥",
      gradient: "from-primary to-healing-green",
      onClick: () => navigate("/listings?type=1"),
      moduleKey: 'hospital',
    },
    {
      icon: Pill,
      title: "Pharmacy",
      description: "Locate nearby pharmacies for medicines & health products.",
      count: "1500+",
      emoji: "💊",
      gradient: "from-healing-green to-healing-green-light",
      onClick: () => navigate("/listings?type=3"),
      moduleKey: 'medical_store',
    },
    {
      icon: FlaskConical,
      title: "Diagnosis Centre",
      description: "Book tests at certified diagnostic labs with quick reports.",
      count: "300+",
      emoji: "🔬",
      gradient: "from-primary to-medical-blue-light",
      onClick: () => navigate("/listings?type=6"),
      moduleKey: 'diagnostic',
    },
    {
      icon: Package,
      title: "Wholesaler",
      description: "Find trusted medical wholesalers for bulk medicines.",
      count: "400+",
      emoji: "📦",
      gradient: "from-cta-orange to-cta-orange-light",
      onClick: () => navigate("/listings?type=7"),
      moduleKey: 'wholesaler',
    },
    {
      icon: Droplets,
      title: "Blood Bank",
      description: "Search blood banks with real-time stock availability.",
      count: "100+",
      emoji: "🩸",
      gradient: "from-destructive to-cta-orange",
      onClick: () => navigate("/blood-banks"),
      moduleKey: 'blood_bank',
    },
    {
      icon: CalendarHeart,
      title: "Health Camp",
      description: "Free health checkup camps — eye, dental, cardiac & more.",
      count: "50+",
      emoji: "⛺",
      gradient: "from-healing-green to-primary",
      onClick: () => navigate("/health-camps"),
      moduleKey: 'health_camp',
    },
    {
      icon: Truck,
      title: "Ambulance",
      description: "Emergency & scheduled ambulance services 24x7.",
      count: "200+",
      emoji: "🚑",
      gradient: "from-cta-orange to-destructive",
      onClick: () => navigate("/ambulance"),
      moduleKey: 'ambulance',
    },
    {
      icon: Heart,
      title: "HomeCare+",
      description: "Verified home nurses, caretakers & elderly care professionals.",
      count: "100+",
      emoji: "❤️",
      gradient: "from-healing-green to-healing-green-light",
      onClick: () => navigate("/homecare"),
      moduleKey: 'homecare',
    },
  ];

  const categories = allCategories.filter(c => isModuleVisible(c.moduleKey));

  return (
    <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-gradient-to-b from-background via-background to-primary/5 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-healing-green/5 rounded-full blur-3xl" />

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-block px-5 py-2 rounded-pill text-sm font-medium mb-4 relative"
            style={{ background: 'linear-gradient(90deg, hsl(213 69% 39% / 0.1), hsl(152 62% 44% / 0.1))' }}
          >
            <span className="text-gradient font-semibold">🧭 Browse Categories</span>
          </motion.span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Explore{' '}
            <span className="text-gradient">Healthcare</span>{' '}
            Categories
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Browse through our comprehensive directory of healthcare services across India
          </p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          variants={{
            hidden: { opacity: 0 },
            visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
          }}
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-5 md:gap-6"
        >
          {categories.map((category, idx) => (
            <motion.div
              key={category.title}
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
              }}
              whileHover={{ y: -5, boxShadow: '0 20px 40px -10px rgba(30,90,168,0.18)' }}
              onClick={category.onClick}
              className="group cursor-pointer bg-card p-5 border border-border/50 shadow-card transition-all duration-300 flex flex-col items-center text-center"
              style={{ borderRadius: 'var(--ui-category-radius)' }}
            >
              {/* Icon with gradient background */}
              <motion.div
                className={`w-16 h-16 bg-gradient-to-br ${category.gradient} flex items-center justify-center mb-4 shadow-lg`}
                style={{ borderRadius: 'var(--ui-category-icon-radius, 16px)' }}
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                <category.icon className="w-8 h-8 text-white" />
              </motion.div>

              {/* Title */}
              <h3 className="text-base font-semibold text-foreground mb-1 group-hover:text-primary transition-colors">
                {category.emoji} {category.title}
              </h3>
              <p className="text-muted-foreground text-xs mb-3 line-clamp-2 leading-relaxed hidden sm:block">
                {category.description}
              </p>

              {/* Count + Arrow */}
              <div className="flex items-center gap-2 mt-auto">
                <span className="text-xs font-medium text-primary">{category.count}</span>
                <motion.div
                  className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center"
                  whileHover={{ x: 3, backgroundColor: 'hsl(213 69% 39%)' }}
                >
                  <ArrowRight className="w-3 h-3 text-primary group-hover:text-white transition-colors" />
                </motion.div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      <PatientAuthModal 
        isOpen={showPatientAuth} 
        onClose={() => setShowPatientAuth(false)}
        onSuccess={() => navigate('/patient/files')}
      />
    </section>
  );
};

export default Categories;
